// Scrollable response table with sortable headers, select-all, keyphrase highlighting, and load-more.
// Pure presentation; all data and handlers are props.

import {
    Button,
    Typography,
} from "@material-tailwind/react";
import { highlightKeyphrases } from "utils/utils";
import { renderSortableHeader } from "components/ui/sortable-header";
import {
    GobbyTable,
    GobbyTableHead,
    GobbyTableBody,
    GobbyTableRow,
    GobbyTableHeader,
    GobbyTableCell,
} from "components/ui/gobby-table";
import FormikCheckbox from "components/form/formik-checkbox";
import SentimentBar from "../dialog/sentiment-bar";

export default function ResponsesTable({
    selectedRows,
    allResponses,
    filters,
    sortedResponses,
    sortField,
    sortDirection,
    gobbyAssist,
    onSelectAll,
    onSelectRow,
    onSort,
    onAddKeyword,
    onOpenResponseThemes,
    customResponsesFilterPager,
    customResponsesFilterSize,
    customResponsesFilterSetSize,
    customResponsesFilterIsReachingEnd,
    customResponsesFilterLoading,
}) {
    const filtersActive =
        filters?.questions?.length > 0 ||
        filters?.themes?.length > 0 ||
        filters?.texts?.length > 0 ||
        filters?.keywords?.length > 0 ||
        filters?.unthemed;

    return (
        <div className="card-inner !bg-white my-4">
            {/* Shared scrollable container for synchronized scrolling */}
            <div className="overflow-auto w-full" style={{ maxHeight: 500 }}>
                <GobbyTable>
                    <GobbyTableHead>
                        <GobbyTableRow>
                            <GobbyTableHeader>
                                <div className="flex items-center gap-2">
                                    <FormikCheckbox
                                        name="select_all"
                                        checked={
                                            selectedRows.length ===
                                                allResponses.length
                                        }
                                        onChange={(e) =>
                                            onSelectAll(e.target.checked)
                                        }
                                        label="Select"
                                        labelProps={{
                                            className: "text-primary",
                                        }}
                                    />
                                </div>
                            </GobbyTableHeader>
                            {renderSortableHeader(
                                "Question",
                                "question",
                                sortField,
                                sortDirection,
                                onSort,
                            )}
                            {renderSortableHeader(
                                "Response",
                                "answer_text",
                                sortField,
                                sortDirection,
                                onSort,
                            )}
                            {renderSortableHeader(
                                "Matched Responses",
                                "matches",
                                sortField,
                                sortDirection,
                                onSort,
                            )}
                            {!!gobbyAssist && (
                                <>
                                    {renderSortableHeader(
                                        "Gobby Score",
                                        "gobby_score",
                                        sortField,
                                        sortDirection,
                                        onSort,
                                    )}
                                    {renderSortableHeader(
                                        "Sentiment",
                                        "sentiment",
                                        sortField,
                                        sortDirection,
                                        onSort,
                                    )}
                                </>
                            )}
                            {renderSortableHeader(
                                "Theme Count",
                                "theme_count",
                                sortField,
                                sortDirection,
                                onSort,
                            )}
                        </GobbyTableRow>
                    </GobbyTableHead>
                    <GobbyTableBody>
                        {!!(filtersActive && sortedResponses?.length < 1) && (
                            <GobbyTableRow>
                                <GobbyTableCell colSpan={gobbyAssist ? 7 : 5}>
                                    <Typography variant="paragraph">
                                        There are no responses available
                                        for the filters selected
                                    </Typography>
                                </GobbyTableCell>
                            </GobbyTableRow>
                        )}
                        {!!(filtersActive && !sortedResponses) && (
                            <GobbyTableRow>
                                <GobbyTableCell colSpan={gobbyAssist ? 7 : 5}>
                                    <Typography variant="paragraph">
                                        Select Questions, Themes, or Unthemed
                                        to show related responses
                                    </Typography>
                                </GobbyTableCell>
                            </GobbyTableRow>
                        )}
                        {!!(filtersActive && sortedResponses) && (
                            <>
                                {sortedResponses?.map(
                                    (response) => (
                                        <GobbyTableRow key={response.answer_nanoid}>
                                            <GobbyTableCell>
                                                <FormikCheckbox
                                                    name={`row_${response.answer_nanoid}`}
                                                    checked={selectedRows.includes(
                                                        response.answer_nanoid,
                                                    )}
                                                    onChange={(e) =>
                                                        onSelectRow(
                                                            response.answer_nanoid,
                                                            e.target
                                                                .checked,
                                                        )
                                                    }
                                                />
                                            </GobbyTableCell>
                                            <GobbyTableCell>
                                                <Typography variant="paragraph">
                                                    {
                                                        response.question_position
                                                    }
                                                </Typography>
                                            </GobbyTableCell>
                                            <GobbyTableCell className="max-w-md">
                                                <Typography variant="paragraph">
                                                    {response.keyphrases &&
                                                gobbyAssist
                                                        ? highlightKeyphrases(
                                                            response.answer_text,
                                                            response.keyphrases,
                                                            onAddKeyword,
                                                        )
                                                        : response.answer_text}
                                                </Typography>
                                            </GobbyTableCell>
                                            <GobbyTableCell>
                                                <Typography variant="paragraph">
                                                    {
                                                        response.times_chosen
                                                    }
                                                </Typography>
                                            </GobbyTableCell>
                                            {!!gobbyAssist && (
                                                <>
                                                    <GobbyTableCell>
                                                        <Typography variant="paragraph">
                                                            {Math.trunc(
                                                                response?.gobby_score,
                                                            )}
                                                        </Typography>
                                                    </GobbyTableCell>
                                                    <GobbyTableCell>
                                                        <div className="flex justify-center gap-2">
                                                            <SentimentBar
                                                                sentiment={
                                                                    response.sentiment
                                                                }
                                                                themeCount={
                                                                    response.theme_count
                                                                }
                                                            />
                                                        </div>
                                                    </GobbyTableCell>
                                                </>
                                            )}
                                            <GobbyTableCell>
                                                <Button
                                                    type="button"
                                                    className="btn btn--secondary !bg-platinumdark"
                                                    onClick={() =>
                                                        onOpenResponseThemes(
                                                            response.answer_nanoid,
                                                        )
                                                    }
                                                >
                                                    {
                                                        response.theme_count
                                                    }
                                                </Button>
                                            </GobbyTableCell>
                                        </GobbyTableRow>
                                    ),
                                )}
                            </>
                        )}
                    </GobbyTableBody>
                </GobbyTable>
            </div>

            {!!(
                customResponsesFilterPager?.current_page !==
                customResponsesFilterPager?.last_page
            ) && (
                <Button
                    onClick={() =>
                        customResponsesFilterSetSize(
                            customResponsesFilterSize + 1,
                        )
                    }
                    className="btn btn--primary mt-2"
                    disabled={customResponsesFilterIsReachingEnd}
                >
                    {customResponsesFilterIsReachingEnd
                        ? "No More Responses"
                        : "Load More Responses"}
                </Button>
            )}
            {/* Optional loading indicator */}
            {customResponsesFilterLoading && (
                <div className="text-center py-4 text-gray-500">
                    Loading more responses...
                </div>
            )}
        </div>
    );
}
